/**
 * Product Combiner Component
 * Handles navigation between combined/linked products
 * Does NOT interfere with cart/checkout - simply navigates to product pages
 */

class ProductCombiner extends HTMLElement {
  constructor() {
    super();
    this.swatches = [];
    this.currentProductId = null;
  }

  connectedCallback() {
    this.currentProductId = this.dataset.currentProduct;
    this.swatches = Array.from(this.querySelectorAll('.product-combiner__swatch'));
    
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', this.handleSwatchClick.bind(this));
      swatch.addEventListener('keydown', this.handleKeyDown.bind(this));
    });

    // Preload images on hover for faster navigation
    this.swatches.forEach(swatch => {
      swatch.addEventListener('mouseenter', this.preloadProduct.bind(this));
    });
  }

  disconnectedCallback() {
    this.swatches.forEach(swatch => {
      swatch.removeEventListener('click', this.handleSwatchClick.bind(this));
      swatch.removeEventListener('keydown', this.handleKeyDown.bind(this));
      swatch.removeEventListener('mouseenter', this.preloadProduct.bind(this));
    });
  }

  /**
   * Handle swatch click - navigate to product page
   * @param {Event} event 
   */
  handleSwatchClick(event) {
    const swatch = event.currentTarget;
    
    // Don't prevent default - let the link navigate naturally
    // This ensures proper browser history and SEO
    
    // If it's the active swatch, prevent navigation
    if (swatch.classList.contains('product-combiner__swatch--active')) {
      event.preventDefault();
      return;
    }

    // Check if product is unavailable and user preference
    if (swatch.classList.contains('product-combiner__swatch--unavailable')) {
      // Still allow navigation to unavailable products
      // User can see product details and sign up for notifications
    }

    // Add loading state for visual feedback
    swatch.classList.add('product-combiner__swatch--loading');
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event 
   */
  handleKeyDown(event) {
    const currentIndex = this.swatches.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.swatches.length;
        event.preventDefault();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.swatches.length) % this.swatches.length;
        event.preventDefault();
        break;
      case 'Home':
        nextIndex = 0;
        event.preventDefault();
        break;
      case 'End':
        nextIndex = this.swatches.length - 1;
        event.preventDefault();
        break;
      default:
        return;
    }

    this.swatches[nextIndex].focus();
  }

  /**
   * Preload product page on hover for faster navigation
   * @param {Event} event 
   */
  preloadProduct(event) {
    const swatch = event.currentTarget;
    const href = swatch.getAttribute('href');
    
    if (!href || swatch.classList.contains('product-combiner__swatch--active')) {
      return;
    }

    // Use prefetch link for browser-level preloading
    if (!document.querySelector(`link[href="${href}"]`)) {
      const prefetchLink = document.createElement('link');
      prefetchLink.rel = 'prefetch';
      prefetchLink.href = href;
      document.head.appendChild(prefetchLink);
    }
  }
}

// Register custom element
if (!customElements.get('product-combiner')) {
  customElements.define('product-combiner', ProductCombiner);
}
